import React from 'react';

/**
 * Tiny Markdown renderer for Pulse assistant responses.
 *
 * Supports a focused subset:
 *  - # / ## / ### headings
 *  - **bold** and *italic*
 *  - `inline code`
 *  - bullet lists (- or *) and numbered lists (1.)
 *  - paragraph breaks
 *
 * No external dependencies.
 */

function renderInline (text, keyPrefix = 'i', ctx = {}) {
  // Order: code > bold > italic
  const parts = [];
  let remaining = text;
  let idx = 0;

  const patterns = [
    { regex: /`([^`]+)`/, render: (m, k) => <code key={k} className="md-code">{m[1]}</code> },
    {
      regex: /\[([^\]]+)\]\(([^)\s]+)\)/,
      render: (m, k) => {
        // Only allow same-origin paths or http(s) URLs — never javascript: etc.
        const href = ctx.resolveHref ? ctx.resolveHref(m[2]) : m[2];
        const safe = href.startsWith('/') || /^https?:\/\//i.test(href);
        if (!safe) return <span key={k}>{m[1]}</span>;
        const external = /^https?:\/\//i.test(href);
        return (
          <a
            key={k}
            href={href}
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            {m[1]}
          </a>
        );
      }
    },
    { regex: /\*\*([^*]+)\*\*/, render: (m, k) => <strong key={k}>{m[1]}</strong> },
    { regex: /\*([^*]+)\*/, render: (m, k) => <em key={k}>{m[1]}</em> }
  ];

  // Simple sequential scan: find earliest match across patterns each iteration.
  while (remaining.length > 0) {
    let earliest = null;
    for (const p of patterns) {
      const m = p.regex.exec(remaining);
      if (m && (earliest === null || m.index < earliest.match.index)) {
        earliest = { match: m, pattern: p };
      }
    }
    if (!earliest) {
      parts.push(remaining);
      break;
    }
    const { match, pattern } = earliest;
    if (match.index > 0) parts.push(remaining.slice(0, match.index));
    parts.push(pattern.render(match, `${keyPrefix}-${idx++}`));
    remaining = remaining.slice(match.index + match[0].length);
  }
  return parts;
}

export default function Markdown ({ children, resolveHref }) {
  if (!children || typeof children !== 'string') return null;
  const ctx = { resolveHref };
  const lines = children.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Blank line
    if (!line.trim()) { i++; continue; }

    // Heading (support #–###### ; deeper levels clamp to h3 styling)
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      const level = Math.min(h[1].length, 3);
      const Tag = `h${level + 2}`; // h3-h5 to keep page hierarchy
      blocks.push(
        <Tag key={`b${key++}`} className={`md-h md-h${level}`}>
          {renderInline(h[2], `h${key}`, ctx)}
        </Tag>
      );
      i++;
      continue;
    }

    // Bullet list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        const itemText = lines[i].replace(/^\s*[-*]\s+/, '');
        items.push(
          <li key={`li${key++}`}>{renderInline(itemText, `li${key}`, ctx)}</li>
        );
        i++;
      }
      blocks.push(<ul key={`b${key++}`} className="md-ul">{items}</ul>);
      continue;
    }

    // Numbered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        const itemText = lines[i].replace(/^\s*\d+\.\s+/, '');
        items.push(
          <li key={`li${key++}`}>{renderInline(itemText, `li${key}`, ctx)}</li>
        );
        i++;
      }
      blocks.push(<ol key={`b${key++}`} className="md-ol">{items}</ol>);
      continue;
    }

    // GFM pipe table: header row + separator row (---) + data rows
    const isSeparatorRow = (l) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(l);
    if (
      line.includes('|') &&
      i + 1 < lines.length &&
      isSeparatorRow(lines[i + 1])
    ) {
      const splitCells = (l) => {
        let s = l.trim();
        if (s.startsWith('|')) s = s.slice(1);
        if (s.endsWith('|')) s = s.slice(0, -1);
        return s.split('|').map((c) => c.trim());
      };
      const headers = splitCells(line);
      i += 2; // consume header + separator
      const rows = [];
      while (i < lines.length && lines[i].includes('|') && !isSeparatorRow(lines[i])) {
        rows.push(splitCells(lines[i]));
        i++;
      }
      blocks.push(
        <table key={`b${key++}`} className="md-table">
          <thead>
            <tr>
              {headers.map((h, hi) => (
                <th key={hi}>{renderInline(h, `th${key}-${hi}`, ctx)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri}>
                {headers.map((_, ci) => (
                  <td key={ci}>{renderInline(r[ci] ?? '', `td${key}-${ri}-${ci}`, ctx)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
      continue;
    }

    // Paragraph (consume consecutive non-empty, non-special lines)
    const paraLines = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^(#{1,6})\s+/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={`b${key++}`} className="md-p">
        {renderInline(paraLines.join(' '), `p${key}`, ctx)}
      </p>
    );
  }

  return <div className="md-root">{blocks}</div>;
}
